import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api/client'
import { getApiBaseUrl } from '@/api/base-url'
import { useAuthSession } from '@/modules/auth/public'

interface ApiError {
  response?: {
    status?: number
  }
}

export interface SupplierUser {
  id: string
  email: string
  phone?: string
  businessDetails?: {
    legalName?: string
    inn?: string
  }
  supplier: {
    id: string
    legalName: string
    inn: string
    status: 'pending' | 'approved' | 'rejected'
    rating: number
    reviewCount: number
  }
  role: 'admin' | 'manager' | 'employee'
  permissions: string[]
  createdAt: string
}

export const useSupplierAuthStore = defineStore('supplierAuth', () => {
  const authSession = useAuthSession()

  // Состояние
  const user = ref<SupplierUser | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Геттеры. Сессия общая с основным магазином - отдельного токена поставщика больше нет:
  // backend выдаёт один и тот же JWT и для /api/auth/login, и для /api/supplier-auth/login,
  // доступ к /api/supplier/* решается только по роли ROLE_SUPPLIER в токене.
  const token = computed(() => authSession.token.value)
  const isAuthenticated = computed(
    () => authSession.isAuthenticated.value && authSession.isSupplier.value,
  )
  const supplierStatus = computed(() => user.value?.supplier?.status || null)
  const isApproved = computed(() => supplierStatus.value === 'approved')
  const isPending = computed(() => supplierStatus.value === 'pending')
  const isRejected = computed(() => supplierStatus.value === 'rejected')
  const permissions = computed(() => user.value?.permissions || [])

  // Проверка разрешений
  const hasPermission = (permission: string): boolean => {
    return permissions.value.includes(permission)
  }

  const normalizeProfileResponse = (data: Record<string, unknown>): SupplierUser => {
    const profileUser = (data['user'] ?? data) as Record<string, unknown>
    const profileSupplier = (data['supplier'] ?? profileUser['supplier'] ?? {}) as Record<
      string,
      unknown
    >
    const businessDetails = (profileSupplier['business_details'] ??
      profileSupplier['businessDetails'] ??
      {}) as Record<string, unknown>
    const legalName =
      typeof businessDetails['legalName'] === 'string'
        ? businessDetails['legalName']
        : typeof businessDetails['legal_name'] === 'string'
          ? businessDetails['legal_name']
          : undefined
    const businessInn =
      typeof businessDetails['inn'] === 'string' ? businessDetails['inn'] : undefined

    const normalized: SupplierUser = {
      id: String(profileUser['id'] ?? ''),
      email: String(profileUser['email'] ?? ''),
      supplier: {
        id: String(profileSupplier['id'] ?? ''),
        legalName:
          typeof profileSupplier['legalName'] === 'string'
            ? profileSupplier['legalName']
            : legalName !== undefined
              ? legalName
              : 'Поставщик',
        inn: typeof profileSupplier['inn'] === 'string' ? profileSupplier['inn'] : '',
        status:
          profileSupplier['status'] === 'approved' ||
          profileSupplier['status'] === 'pending' ||
          profileSupplier['status'] === 'rejected'
            ? profileSupplier['status']
            : 'pending',
        rating: typeof profileSupplier['rating'] === 'number' ? profileSupplier['rating'] : 0,
        reviewCount:
          typeof profileSupplier['reviewCount'] === 'number'
            ? profileSupplier['reviewCount']
            : typeof profileSupplier['review_count'] === 'number'
              ? profileSupplier['review_count']
              : 0,
      },
      role:
        profileUser['role'] === 'admin' ||
        profileUser['role'] === 'manager' ||
        profileUser['role'] === 'employee'
          ? profileUser['role']
          : 'admin',
      permissions: Array.isArray(profileUser['permissions'])
        ? profileUser['permissions'].map(String)
        : [],
      createdAt:
        typeof profileUser['createdAt'] === 'string'
          ? profileUser['createdAt']
          : typeof profileUser['created_at'] === 'string'
            ? profileUser['created_at']
            : '',
    }

    if (typeof profileUser['phone'] === 'string') {
      normalized.phone = profileUser['phone']
    }

    if (legalName !== undefined || businessInn !== undefined) {
      normalized.businessDetails = {}
      if (legalName !== undefined) {
        normalized.businessDetails.legalName = legalName
      }
      if (businessInn !== undefined) {
        normalized.businessDetails.inn = businessInn
      }
    }

    return normalized
  }

  // Подгружаем профиль компании/права поставщика поверх уже существующей основной сессии
  const loadProfile = async (): Promise<boolean> => {
    if (!isAuthenticated.value) return false

    try {
      const apiUrl = getApiBaseUrl()
      const response = await apiClient.get(`${apiUrl}/api/supplier-auth/profile`)

      if (response.data.success) {
        user.value = normalizeProfileResponse(response.data.data)
        return true
      }
      return false
    } catch (e: unknown) {
      const status = (e as ApiError).response?.status
      if (status === 401) {
        user.value = null
        await authSession.logout()
        return false
      }

      console.error('Ошибка загрузки профиля поставщика:', e)
      return false
    }
  }

  // Инициализация при загрузке приложения - больше не читает отдельный supplier_token,
  // просто подгружает данные профиля, если уже залогинены как поставщик.
  const initAuth = async () => {
    if (isAuthenticated.value) {
      await loadProfile()
    }
  }

  // Выход - это выход из общей сессии, аккаунт один
  const logout = async () => {
    loading.value = true
    try {
      await authSession.signOut()
    } finally {
      user.value = null
      error.value = null
      loading.value = false
    }
  }

  // Получение статистики поставщика
  const getSupplierStats = async () => {
    try {
      const apiUrl = getApiBaseUrl()
      const response = await apiClient.get(`${apiUrl}/api/supplier-auth/dashboard-stats`)

      if (response.data.success) {
        return response.data.data
      } else {
        throw new Error(response.data.message || 'Ошибка получения статистики')
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Ошибка получения статистики'
      error.value = errorMessage
      throw new Error(errorMessage)
    }
  }

  // Получение статистики дашборда
  const getDashboardStats = async () => {
    try {
      const apiUrl = getApiBaseUrl()
      const response = await apiClient.get(`${apiUrl}/api/supplier-auth/dashboard-stats`)

      if (response.data.success) {
        return response.data
      } else {
        throw new Error(response.data.message || 'Ошибка получения статистики')
      }
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Ошибка получения статистики'
      error.value = errorMessage
      throw new Error(errorMessage)
    }
  }

  return {
    // Состояние
    user,
    token,
    loading,
    error,

    // Геттеры
    isAuthenticated,
    supplierStatus,
    isApproved,
    isPending,
    isRejected,
    permissions,

    // Действия
    initAuth,
    loadProfile,
    logout,
    getSupplierStats,
    getDashboardStats,
    hasPermission,
  }
})
